import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { ConfiguredTariffV2, RateV2 } from "@voltie/localization-web";

import { serverErrors, useT, type Translate } from "@/app/localization";
import { isUnitTariff, operatingCurrencyKey } from "@/components/OrgOperatingCurrency";
import { PricingForm } from "@/components/PricingForm";
import { AlertTriangle, ChevronRight } from "@/components/icons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { activateOperatingCurrencySwitch, fetchOperatingCurrencySwitchBasis, repairOperatingCurrencySwitch } from "@/lib/api";
import { summarizeTariff, tariffMode } from "@/lib/moneyManagement";
import {
  CONSUMER_PATHS,
  CONSUMER_STATES,
  buildSwitchRequest,
  classifySwitchRefusal,
  copyAllSwitchItemRules,
  copySwitchItemRules,
  createSwitchDraft,
  enterSwitchItem,
  nextSwitchRepairEvidence,
  noChargeSwitchItem,
  rebaseSwitchDraft,
  resolveUnknownSwitchActivation,
  selectSwitchTarget,
  setSwitchConfirmation,
  setSwitchConsumerState,
  setSwitchScopeEvidence,
  setSwitchScopeSuspended,
  switchDraftProblems,
  switchRepairOffer,
  type OperatingCurrencySwitchBasis,
  type OperatingCurrencySwitchCommitted,
  type OperatingCurrencySwitchDraft,
  type OperatingCurrencySwitchRepaired,
  type OperatingCurrencySwitchRequest,
  type SwitchDraftEntry,
  type SwitchDraftProblem,
  type SwitchDraftResult,
  type SwitchInvalidReason,
  type SwitchItemRef,
  type SwitchProjectionReport,
  type SwitchRefusal,
  type SwitchRepairEvidence,
  type SwitchStoredTariff,
} from "@/lib/operatingCurrencySwitch";
import { ruleRate, validatePricingForm, type PricingFormValues } from "@/lib/pricingForm";

/** Query key of one account's prepared switch basis of one organization. */
export const operatingCurrencySwitchKey = (account: number | null, orgId: number) =>
  ["organization-operating-currency-switch", account, orgId] as const;

const originOf = (account: number, orgId: number) => `${account}:${orgId}`;

/** An item named by its basis identity; a profile keeps the name it had when the answer arrived. */
type ItemName = { readonly kind: "unit"; readonly unit_id: string } | { readonly kind: "profile"; readonly name: string };

type Activation =
  | { readonly kind: "idle" }
  | { readonly kind: "pending" }
  /** The activation may have committed: frozen until one prepare re-read resolves it. */
  | { readonly kind: "unknown"; readonly request: OperatingCurrencySwitchRequest; readonly check: "running" | "failed" };

/** What the dialog says about the last activation answer: kinds and safe fields only. */
type Notice =
  | { readonly kind: "refusal"; readonly refusal: SwitchRefusal; readonly target: string; readonly item: ItemName | null; readonly itemRef: SwitchItemRef | null }
  | { readonly kind: "not_committed"; readonly currency: string }
  | { readonly kind: "changed" };

/** The last switch outcome of an origin, shown in the section after the dialog closes. */
type Result =
  | { readonly kind: "committed"; readonly currency: string; readonly projection: SwitchProjectionReport }
  | { readonly kind: "unknown_committed"; readonly currency: string }
  | { readonly kind: "repaired"; readonly projection: SwitchProjectionReport }
  | { readonly kind: "repair_superseded" }
  | { readonly kind: "repair_unconfirmed" }
  | { readonly kind: "repair_refused"; readonly refusal: SwitchRefusal };

/** Everything the switch keeps for one origin (`${account}:${orgId}`), in React state only. */
interface OriginState {
  readonly draft: OperatingCurrencySwitchDraft | null;
  readonly view: "edit" | "review";
  readonly editing: SwitchItemRef | null;
  readonly activation: Activation;
  readonly notice: Notice | null;
  readonly result: Result | null;
  readonly evidence: SwitchRepairEvidence | null;
  readonly repairing: boolean;
}

const IDLE: Activation = { kind: "idle" };
const INITIAL: OriginState = { draft: null, view: "edit", editing: null, activation: IDLE, notice: null, result: null, evidence: null, repairing: false };

interface ActivationVars {
  readonly account: number;
  readonly orgId: number;
  readonly request: OperatingCurrencySwitchRequest;
}

interface RepairVars {
  readonly account: number;
  readonly orgId: number;
  readonly revision: number;
}

type Reload = "operating" | "basis";

// Section 7.1 of the S4 preflight: what each refusal may have made stale. A refusal wrote nothing.
const REFUSAL_RELOADS: Partial<Record<SwitchRefusal["code"], readonly Reload[]>> = {
  organization_not_found: ["operating"],
  operating_currency_not_assignable: ["basis"],
  operating_currency_switch_same_currency: ["operating", "basis"],
  operating_currency_switch_stale: ["operating", "basis"],
  operating_currency_switch_basis_unreadable: ["basis"],
  operating_currency_switch_items_changed: ["basis"],
  currency_not_enabled: ["basis"],
  operating_currency_switch_busy: ["basis"],
};

const SCOPE_REASONS: ReadonlySet<SwitchInvalidReason> = new Set([
  "activation_scope_missing", "currency_not_scoped", "consumer_states_invalid", "evidence_invalid",
  "suspended_invalid", "consumer_path_unknown", "consumer_state_unknown", "evidence_path_unknown",
]);

function refusalReloads(refusal: SwitchRefusal): readonly Reload[] {
  // The store was unreadable at the 409 read, or unavailable (503) while starting.
  if (refusal.code === "operating_currency_unavailable") return refusal.status === 409 ? ["operating"] : ["basis"];
  return REFUSAL_RELOADS[refusal.code] ?? [];
}

/**
 * After a committed, unknown or repaired switch: everything that reads the tenant's currency
 * or prices; history stays. The basis is only marked stale: the dialog that used it is closed
 * or frozen, and its next open reads it once.
 */
function invalidateSwitchEffects(queryClient: QueryClient, account: number, orgId: number, { basis = true } = {}) {
  void queryClient.invalidateQueries({ queryKey: operatingCurrencyKey(account, orgId) });
  if (basis) void queryClient.invalidateQueries({ queryKey: operatingCurrencySwitchKey(account, orgId), refetchType: "none" });
  void queryClient.invalidateQueries({ queryKey: ["pricing-profiles", orgId] });
  void queryClient.invalidateQueries({ predicate: isUnitTariff });
  void queryClient.invalidateQueries({ queryKey: ["pricing-context"] });
  void queryClient.invalidateQueries({ queryKey: ["units"] });
}

const failedUnits = (projection: SwitchProjectionReport) =>
  projection.units.filter((u) => u.mirror === "failed" || u.published === "failed").length;

const sameRef = (a: SwitchItemRef, b: SwitchItemRef | null) =>
  b !== null && (a.kind === "unit" ? b.kind === "unit" && a.unit_id === b.unit_id : b.kind === "profile" && a.profile_id === b.profile_id);

const refKey = (ref: SwitchItemRef) => (ref.kind === "unit" ? `unit:${ref.unit_id}` : `profile:${ref.profile_id}`);

function itemNameOf(ref: SwitchItemRef, draft: OperatingCurrencySwitchDraft | null): ItemName {
  if (ref.kind === "unit") return ref;
  const profile = draft?.profiles.find((p) => p.item.profile_id === ref.profile_id);
  return { kind: "profile", name: profile?.item.name ?? String(ref.profile_id) };
}

function itemLabel(t: Translate, item: ItemName): string {
  return item.kind === "unit"
    ? t("operator.orgs.currency.switch.item_unit", { unit: item.unit_id })
    : t("operator.orgs.currency.switch.item_profile", { name: item.name });
}

// 401/403 have shared messages; a 404 or anything without one says the organization can't be read now.
const genericRefusal = (t: Translate, status: number) =>
  serverErrors.message(t, { status }, { fallback: "operator.orgs.currency.unavailable" });

function refusalText(t: Translate, refusal: SwitchRefusal, target: string, item: ItemName | null): string {
  switch (refusal.code) {
    case "organization_not_found":
    case "access_denied":
      return genericRefusal(t, refusal.status);
    case "operating_currency_unavailable":
      return t("operator.orgs.currency.unavailable");
    case "operating_currency_not_assignable":
      return t("operator.orgs.currency.not_assignable", { currency: refusal.currency });
    case "operating_currency_switch_invalid":
      if (item !== null && refusal.reason === "paid_requires_positive_rate" && item.kind === "unit")
        return t("operator.orgs.currency.switch.paid_needs_rate", { unit: item.unit_id, currency: target });
      if (item !== null && refusal.reason === "rate_invalid") return t("operator.orgs.currency.switch.invalid_rate", { item: itemLabel(t, item) });
      if (item !== null) return t("operator.orgs.currency.switch.invalid_item", { item: itemLabel(t, item) });
      return SCOPE_REASONS.has(refusal.reason) ? t("operator.orgs.currency.switch.invalid_scope") : t("operator.orgs.currency.switch.invalid");
    case "operating_currency_switch_same_currency":
      return t("operator.orgs.currency.switch.same_currency", { currency: refusal.effective_currency });
    case "operating_currency_switch_stale":
      return t("operator.orgs.currency.switch.stale");
    case "operating_currency_switch_basis_unreadable":
      return t("operator.orgs.currency.switch.basis_unreadable", { units: refusal.units, profiles: refusal.profiles });
    case "operating_currency_switch_items_changed":
      return t("operator.orgs.currency.switch.items_changed");
    case "currency_not_enabled":
      return t(`operator.orgs.currency.switch.not_enabled_${refusal.reason}`, { currency: refusal.currency });
    case "money_version_2_not_enabled":
      return t("operator.orgs.currency.switch.money_v2_not_enabled");
    case "operating_currency_switch_too_large":
      return t("operator.orgs.currency.switch.too_large", { count: refusal.item_count, limit: refusal.limit });
    case "request_too_large":
      return t("operator.orgs.currency.switch.request_too_large");
    case "operating_currency_switch_busy":
      return t("operator.orgs.currency.switch.busy");
    default:
      return t("operator.orgs.currency.switch.basis_unavailable");
  }
}

function resultText(t: Translate, result: Result): string {
  switch (result.kind) {
    case "committed": {
      if (result.projection.status === "complete") return t("operator.orgs.currency.switch.committed", { currency: result.currency });
      const count = failedUnits(result.projection);
      return count > 0
        ? t("operator.orgs.currency.switch.projection_incomplete_count", { count })
        : t("operator.orgs.currency.switch.projection_incomplete");
    }
    case "unknown_committed":
      return t("operator.orgs.currency.switch.unknown_committed", { currency: result.currency });
    case "repaired":
      return result.projection.status === "complete"
        ? t("operator.orgs.currency.switch.repaired")
        : t("operator.orgs.currency.switch.repair_still_incomplete", { count: failedUnits(result.projection) });
    case "repair_superseded":
      return t("operator.orgs.currency.switch.repair_superseded");
    case "repair_unconfirmed":
      return t("operator.orgs.currency.switch.repair_unconfirmed");
    case "repair_refused":
      return refusalText(t, result.refusal, "", null);
  }
}

const resultSettled = (result: Result) =>
  (result.kind === "committed" || result.kind === "repaired") && result.projection.status === "complete";

const RATE_UNITS: Readonly<Record<string, RateV2["unit"]>> = { kwh: "kWh", hour: "hour", session: "session" };

/** Entered values as a stored tariff of the target, so the review summarizes old and new prices alike. */
function enteredTariff(values: PricingFormValues, currency: string, exponent: number): ConfiguredTariffV2 {
  return {
    tariff_state: "configured",
    currency,
    exponent,
    stored_money_version: 2,
    reason: null,
    rules: values.rules.map((rule, index) => {
      const rate = ruleRate(rule);
      return {
        index,
        fee_type: rule.fee_type,
        uom: rule.uom,
        is_tou: !!rule.is_tou,
        start_time: rule.start_time ?? "",
        end_time: rule.end_time ?? "",
        days: rule.days ?? "",
        priority: rule.priority ?? 0,
        rate: { currency, amount: rate.ok ? rate.value : "0", unit: RATE_UNITS[rule.uom] ?? "kWh", precision_origin: "new_input", money_version: 2 },
      };
    }),
  };
}

const currentPrices = (t: Translate, tariff: SwitchStoredTariff) =>
  tariffMode(tariff) === null ? t("operator.orgs.currency.switch.item_current_unreadable") : summarizeTariff(tariff, t);

type Action = "change" | "change_unassigned";

// The tables' nowrap cells scroll inside this, so they never widen the dialog.
const TABLE_SCROLL = "max-w-full overflow-x-auto";

/**
 * The platform admin's change of an organization's operating currency with new prices
 * (CUR-207 S4d): the section's change action and last outcome, and the dialog that
 * prepares, reviews and activates the switch over the accepted switch routes. Target
 * eligibility, completeness and outcomes are the server's; this only collects explicit
 * new prices, shows the server's answers and never repeats a write on its own. Drafts,
 * outcomes and repair evidence live in React state per account and organization, so a
 * reload or another origin never sees them.
 */
export function OrgOperatingCurrencySwitch({
  orgId, account, currency, action, disabled = false,
}: {
  orgId: number;
  account: number;
  /** The section's current (effective) currency, for the title until the basis is read. */
  currency: string | null;
  /** The change action the section offers now; null hides it but keeps the outcome. */
  action: Action | null;
  disabled?: boolean;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const origin = originOf(account, orgId);
  const id = useId();

  const [states, setStates] = useState<Readonly<Record<string, OriginState>>>({});
  const [openOrigin, setOpenOrigin] = useState<string | null>(null);
  // Synchronous guards per operation and origin: a second click before the next render sends nothing.
  const inFlight = useRef(new Set<string>());
  const open = openOrigin === origin;
  const state = states[origin] ?? INITIAL;

  const update = (from: string, change: (s: OriginState) => OriginState) =>
    setStates((all) => ({ ...all, [from]: change(all[from] ?? INITIAL) }));

  const basisQ = useQuery({
    queryKey: operatingCurrencySwitchKey(account, orgId),
    queryFn: ({ queryKey }) => fetchOperatingCurrencySwitchBasis(queryKey[2]),
    enabled: open,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const basis = basisQ.data?.status === "value" ? basisQ.data.value : null;

  // A newly read basis starts the draft or rebases it; an activation in flight or unresolved keeps it frozen.
  useEffect(() => {
    if (!open || basis === null || basisQ.isFetching) return;
    update(origin, (s) => {
      if (s.activation.kind !== "idle") return s;
      if (s.draft === null) return { ...s, draft: createSwitchDraft(basis) };
      if (s.draft.basis === basis) return s;
      const rebased = rebaseSwitchDraft(s.draft, basis);
      if (!rebased.ok) return s;
      const editing = s.editing !== null && rebased.summary.removed.some((r) => sameRef(r, s.editing)) ? null : s.editing;
      return { ...s, draft: rebased.draft, editing };
    });
  }, [open, origin, basis, basisQ.isFetching]);

  const settleCommitted = (v: ActivationVars, committed: OperatingCurrencySwitchCommitted) => {
    const from = originOf(v.account, v.orgId);
    update(from, (s) => ({
      ...s,
      draft: null,
      view: "edit",
      editing: null,
      activation: IDLE,
      notice: null,
      result: { kind: "committed", currency: committed.operating_currency, projection: committed.projection },
      evidence: nextSwitchRepairEvidence(s.evidence, from, { kind: "committed", committed }),
    }));
    setOpenOrigin((o) => (o === from ? null : o));
    invalidateSwitchEffects(queryClient, v.account, v.orgId);
  };

  // One prepare re-read decides an unknown activation; it never sends the activation again.
  const checkUnknown = async (v: ActivationVars) => {
    const from = originOf(v.account, v.orgId);
    const guard = `check:${from}`;
    if (inFlight.current.has(guard)) return;
    inFlight.current.add(guard);
    update(from, (s) => (s.activation.kind === "unknown" ? { ...s, activation: { ...s.activation, check: "running" } } : s));
    let fresh: OperatingCurrencySwitchBasis | null = null;
    try {
      const read = await queryClient.fetchQuery({
        queryKey: operatingCurrencySwitchKey(v.account, v.orgId),
        queryFn: () => fetchOperatingCurrencySwitchBasis(v.orgId),
        staleTime: 0,
        retry: false,
      });
      if (read.status === "value") fresh = read.value;
    } catch {
      // Stays unknown, with an explicit check.
    } finally {
      inFlight.current.delete(guard);
    }
    if (fresh === null) {
      update(from, (s) => (s.activation.kind === "unknown" ? { ...s, activation: { ...s.activation, check: "failed" } } : s));
      return;
    }
    const basisNow = fresh;
    const resolution = resolveUnknownSwitchActivation(v.orgId, v.request, basisNow);
    update(from, (s) => {
      if (s.activation.kind !== "unknown") return s;
      if (resolution.outcome === "committed") {
        return {
          ...s,
          draft: null,
          view: "edit",
          editing: null,
          activation: IDLE,
          notice: null,
          result: { kind: "unknown_committed", currency: resolution.operating_currency },
          evidence: nextSwitchRepairEvidence(s.evidence, from, { kind: "unknown_resolved", resolution }),
        };
      }
      const rebased = s.draft === null ? null : rebaseSwitchDraft(s.draft, basisNow);
      return {
        ...s,
        draft: rebased?.ok ? rebased.draft : createSwitchDraft(basisNow),
        view: "edit",
        activation: IDLE,
        notice: resolution.outcome === "not_committed" ? { kind: "not_committed", currency: v.request.target_currency } : { kind: "changed" },
      };
    });
    if (resolution.outcome === "committed") setOpenOrigin((o) => (o === from ? null : o));
  };

  const settleUnknown = (v: ActivationVars) => {
    const from = originOf(v.account, v.orgId);
    update(from, (s) => ({ ...s, activation: { kind: "unknown", request: v.request, check: "running" }, notice: null }));
    // The basis is re-read once by the check itself.
    invalidateSwitchEffects(queryClient, v.account, v.orgId, { basis: false });
    void checkUnknown(v);
  };

  const settleRefused = (v: ActivationVars, refusal: SwitchRefusal) => {
    const from = originOf(v.account, v.orgId);
    let itemRef: SwitchItemRef | null = null;
    if (refusal.code === "operating_currency_switch_invalid" && refusal.item !== null) {
      const { kind, index } = refusal.item;
      const unit = kind === "unit" ? v.request.units[index] : undefined;
      const profile = kind === "profile" ? v.request.profiles[index] : undefined;
      if (unit) itemRef = { kind: "unit", unit_id: unit.unit_id };
      if (profile) itemRef = { kind: "profile", profile_id: profile.profile_id };
    }
    update(from, (s) => ({
      ...s,
      activation: IDLE,
      view: refusal.code === "operating_currency_switch_invalid" ? "edit" : s.view,
      notice: { kind: "refusal", refusal, target: v.request.target_currency, itemRef, item: itemRef === null ? null : itemNameOf(itemRef, s.draft) },
    }));
    const reloads = refusalReloads(refusal);
    if (reloads.includes("operating")) void queryClient.invalidateQueries({ queryKey: operatingCurrencyKey(v.account, v.orgId) });
    if (reloads.includes("basis")) void queryClient.invalidateQueries({ queryKey: operatingCurrencySwitchKey(v.account, v.orgId) });
  };

  const activateM = useMutation({
    mutationFn: (v: ActivationVars) => activateOperatingCurrencySwitch(v.orgId, v.request),
    retry: false,
    onSettled: (_data, _error, v) => inFlight.current.delete(`activate:${originOf(v.account, v.orgId)}`),
    onSuccess: (read, v) => (read.status === "value" ? settleCommitted(v, read.value) : settleUnknown(v)),
    onError: (error, v) => {
      const refusal = classifySwitchRefusal(error, "activate");
      if (refusal.code === "unknown" || refusal.code === "operating_currency_switch_commit_unknown") settleUnknown(v);
      else settleRefused(v, refusal);
    },
  });

  const settleRepair = (v: RepairVars, change: (s: OriginState, from: string) => Partial<OriginState>) => {
    const from = originOf(v.account, v.orgId);
    update(from, (s) => ({ ...s, repairing: false, ...change(s, from) }));
  };

  const repairM = useMutation({
    mutationFn: (v: RepairVars) => repairOperatingCurrencySwitch(v.orgId, v.revision),
    retry: false,
    onSettled: (_data, _error, v) => inFlight.current.delete(`repair:${originOf(v.account, v.orgId)}`),
    onSuccess: (read, v) => {
      if (read.status === "value") {
        const repaired: OperatingCurrencySwitchRepaired = read.value;
        settleRepair(v, (s, from) => ({
          result: { kind: "repaired", projection: repaired.projection },
          evidence: nextSwitchRepairEvidence(s.evidence, from, { kind: "repaired", repaired }),
        }));
      } else {
        settleRepair(v, () => ({ result: { kind: "repair_unconfirmed" } }));
      }
      invalidateSwitchEffects(queryClient, v.account, v.orgId);
    },
    onError: (error, v) => {
      const refusal = classifySwitchRefusal(error, "repair");
      if (refusal.code === "operating_currency_switch_repair_stale") {
        // The revision the refused repair asked for, never the one in the answer.
        settleRepair(v, (s, from) => ({
          result: { kind: "repair_superseded" },
          evidence: nextSwitchRepairEvidence(s.evidence, from, { kind: "repair_stale", operating_currency_revision: v.revision }),
        }));
        void queryClient.invalidateQueries({ queryKey: operatingCurrencySwitchKey(v.account, v.orgId) });
      } else if (refusal.code === "unknown") {
        // Resending is idempotent: the evidence and the action stay.
        settleRepair(v, () => ({ result: { kind: "repair_unconfirmed" } }));
        invalidateSwitchEffects(queryClient, v.account, v.orgId);
      } else {
        settleRepair(v, () => ({ result: { kind: "repair_refused", refusal } }));
      }
    },
  });

  const draft = state.draft;
  const frozen = state.activation.kind !== "idle";
  const repairOffer = switchRepairOffer(state.evidence, origin);

  const activate = () => {
    const guard = `activate:${origin}`;
    if (draft === null || frozen || inFlight.current.has(guard)) return;
    const built = buildSwitchRequest(draft);
    if (!built.ok) return;
    inFlight.current.add(guard);
    update(origin, (s) => ({ ...s, activation: { kind: "pending" }, notice: null, result: null }));
    activateM.mutate({ account, orgId, request: built.request });
  };

  const repair = () => {
    const guard = `repair:${origin}`;
    if (repairOffer === null || state.repairing || inFlight.current.has(guard)) return;
    inFlight.current.add(guard);
    update(origin, (s) => ({ ...s, repairing: true }));
    repairM.mutate({ account, orgId, revision: repairOffer.operating_currency_revision });
  };

  /** Applies a draft operation of the open origin; a refused one changes nothing. */
  const edit = (operation: (d: OperatingCurrencySwitchDraft) => SwitchDraftResult, more: Partial<OriginState> = {}) =>
    update(origin, (s) => {
      if (s.draft === null || s.activation.kind !== "idle") return s;
      const result = operation(s.draft);
      return result.ok ? { ...s, ...more, draft: result.draft } : s;
    });

  // ── Section line ──────────────────────────────────────────────────────────────
  const unknown = state.activation.kind === "unknown" ? state.activation : null;
  const unknownText = unknown === null ? null : t("operator.orgs.currency.switch.unknown", { currency: unknown.request.target_currency });
  const checkAgain = unknown?.check === "failed" ? (
    <Button size="sm" variant="outline" onClick={() => void checkUnknown({ account, orgId, request: unknown.request })}>
      {t("operator.orgs.currency.switch.unknown_check_again")}
    </Button>
  ) : null;

  const section = (
    <div className="flex flex-col items-start gap-2" data-testid="org-currency-switch-section">
      {action !== null && (
        <div className="flex flex-col items-start gap-1">
          <Button size="sm" variant="outline" onClick={() => setOpenOrigin(origin)} disabled={disabled || repairOffer !== null}>
            {t(action === "change" ? "operator.orgs.currency.change_button" : "operator.orgs.currency.change_unassigned_button")}
          </Button>
          {action === "change" && <p className="text-xs text-muted-foreground">{t("operator.orgs.currency.change_help")}</p>}
        </div>
      )}
      {state.activation.kind === "pending" && (
        <p role="status" className="text-sm text-muted-foreground">{t("operator.orgs.currency.switch.activating")}</p>
      )}
      {unknownText !== null && (
        <div className="flex flex-col items-start gap-2" data-testid="org-currency-switch-unknown">
          <FieldError>{unknownText}</FieldError>
          {checkAgain}
        </div>
      )}
      {state.result !== null && (resultSettled(state.result)
        ? <p role="status" className="text-sm text-muted-foreground" data-testid="org-currency-switch-result">{resultText(t, state.result)}</p>
        : <FieldError data-testid="org-currency-switch-result">{resultText(t, state.result)}</FieldError>)}
      {repairOffer !== null && (
        <div className="flex flex-col items-start gap-1">
          <Button size="sm" onClick={repair} disabled={state.repairing}>
            {t(state.repairing ? "operator.orgs.currency.switch.repairing" : "operator.orgs.currency.switch.repair_button")}
          </Button>
          <p className="text-xs text-muted-foreground">{t("operator.orgs.currency.switch.repair_help")}</p>
        </div>
      )}
    </div>
  );

  // ── Dialog ────────────────────────────────────────────────────────────────────
  let body: ReactNode;
  const prepareFailure = basisQ.isError
    ? classifySwitchRefusal(basisQ.error, "prepare")
    : basisQ.data?.status === "invalid" ? ({ code: "unknown" } as const) : null;
  const reload = (
    <Button type="button" size="sm" variant="outline" onClick={() => void basisQ.refetch()} disabled={frozen || basisQ.isFetching}>
      {t("operator.orgs.currency.switch.reload")}
    </Button>
  );
  const prepareAlert = prepareFailure !== null && !basisQ.isFetching && (
    <Alert data-testid="org-currency-switch-prepare-failed">
      <AlertTriangle />
      <AlertDescription className="flex flex-col items-start gap-2">
        <span>{refusalText(t, prepareFailure, "", null)}</span>
        {reload}
      </AlertDescription>
    </Alert>
  );

  if (draft === null) {
    body = prepareAlert || (
      <div role="status">
        <Skeleton className="h-24 w-full" />
        <span className="sr-only">{t("common.state.loading")}</span>
      </div>
    );
  } else {
    body = (
      <SwitchDraftView
        id={id}
        t={t}
        state={state}
        draft={draft}
        frozen={frozen}
        checkAgain={checkAgain}
        prepareAlert={prepareAlert || null}
        reload={reload}
        edit={edit}
        setView={(view) => update(origin, (s) => (s.activation.kind === "idle" ? { ...s, view } : s))}
        setEditing={(editing) => update(origin, (s) => ({ ...s, editing }))}
        activate={activate}
      />
    );
  }

  return (
    <>
      {section}
      <Dialog open={open} onOpenChange={(next) => setOpenOrigin(next ? origin : null)}>
        <DialogContent className="max-h-[90vh] grid-cols-1 overflow-y-auto sm:max-w-3xl" data-testid="org-currency-switch-dialog">
          <DialogHeader>
            <DialogTitle>{t("operator.orgs.currency.switch.title", { currency: draft?.basis.effective_currency ?? currency ?? "" })}</DialogTitle>
            <DialogDescription>{t("operator.orgs.currency.change_help")}</DialogDescription>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    </>
  );
}

function SwitchDraftView({
  id, t, state, draft, frozen, checkAgain, prepareAlert, reload, edit, setView, setEditing, activate,
}: {
  id: string;
  t: Translate;
  state: OriginState;
  draft: OperatingCurrencySwitchDraft;
  frozen: boolean;
  checkAgain: ReactNode;
  prepareAlert: ReactNode;
  reload: ReactNode;
  edit: (operation: (d: OperatingCurrencySwitchDraft) => SwitchDraftResult, more?: Partial<OriginState>) => void;
  setView: (view: OriginState["view"]) => void;
  setEditing: (ref: SwitchItemRef | null) => void;
  activate: () => void;
}) {
  const { basis, target } = draft;
  const old = basis.effective_currency;
  const choice = target === null ? undefined : basis.target_choices.find((c) => c.currency === target);
  const problems = switchDraftProblems(draft);
  const reviewing = state.view === "review" && problems.length === 0;
  const eligible = basis.target_choices.some((c) => c.verdict === "eligible");
  const notice = state.notice;
  const highlighted = notice?.kind === "refusal" ? notice.itemRef : null;
  const scopeRefused = notice?.kind === "refusal" && notice.refusal.code === "operating_currency_switch_invalid"
    && notice.itemRef === null && SCOPE_REASONS.has(notice.refusal.reason);

  const noticeText = notice === null ? null
    : notice.kind === "refusal" ? refusalText(t, notice.refusal, notice.target, notice.item)
      : notice.kind === "not_committed" ? t("operator.orgs.currency.switch.unknown_not_committed", { currency: notice.currency })
        : t("operator.orgs.currency.switch.stale");

  const items: { ref: SwitchItemRef; entry: SwitchDraftEntry; name: ItemName; tariff: SwitchStoredTariff; paidMode: string | null }[] = [
    ...draft.units.map(({ item, entry }) => ({ ref: { kind: "unit", unit_id: item.unit_id } as SwitchItemRef, entry, name: { kind: "unit", unit_id: item.unit_id } as ItemName, tariff: item.tariff, paidMode: item.pricing_mode })),
    ...draft.profiles.map(({ item, entry }) => ({ ref: { kind: "profile", profile_id: item.profile_id } as SwitchItemRef, entry, name: { kind: "profile", name: item.name } as ItemName, tariff: item.tariff, paidMode: null })),
  ];
  const problemsOf = (ref: SwitchItemRef) => problems.filter((p): p is Extract<SwitchDraftProblem, { item: SwitchItemRef }> => "item" in p && sameRef(p.item, ref));

  const unknown = state.activation.kind === "unknown" && (
    <Alert data-testid="org-currency-switch-dialog-unknown">
      <AlertTriangle />
      <AlertDescription className="flex flex-col items-start gap-2">
        <span>{t("operator.orgs.currency.switch.unknown", { currency: state.activation.request.target_currency })}</span>
        {state.activation.check === "running"
          ? <span role="status" className="text-muted-foreground">{t("common.state.loading")}</span>
          : checkAgain}
      </AlertDescription>
    </Alert>
  );

  const header = (
    <>
      {unknown}
      {prepareAlert}
      {noticeText !== null && (
        <Alert data-testid="org-currency-switch-notice">
          <AlertTriangle />
          <AlertDescription>{noticeText}</AlertDescription>
        </Alert>
      )}
    </>
  );

  if (reviewing && target !== null) {
    const exponent = choice?.exponent ?? 2;
    const headingId = `${id}-review-heading`;
    return (
      <div className="flex flex-col gap-4">
        {header}
        <fieldset disabled={frozen} className="flex min-w-0 flex-col gap-4" data-testid="org-currency-switch-review">
          <h5 id={headingId} className="font-medium">{t("operator.orgs.currency.switch.review_heading", { old, currency: target })}</h5>
          <div className="flex flex-col gap-1">
            <p>{t("operator.orgs.currency.switch.review_summary", { units: draft.units.length, profiles: draft.profiles.length })}</p>
            <p>{t("operator.orgs.currency.switch.review_no_conversion", { old })}</p>
            <p>{t("operator.orgs.currency.switch.review_new_sessions_only")}</p>
          </div>
          {/* Nothing in this table takes focus, so the keyboard scrolls it from here. */}
          <div
            role="region"
            aria-labelledby={headingId}
            tabIndex={0}
            className={`${TABLE_SCROLL} rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50`}
          >
            <Table>
              <TableBody>
                {items.map(({ ref, entry, name, tariff }) => (
                  <TableRow key={refKey(ref)} data-testid={`org-currency-switch-review-${refKey(ref)}`}>
                    <TableCell className="font-medium">{itemLabel(t, name)}</TableCell>
                    <TableCell className="whitespace-normal">{currentPrices(t, tariff)}</TableCell>
                    <TableCell><ChevronRight className="h-4 w-4" aria-hidden /></TableCell>
                    <TableCell className="whitespace-normal">
                      {entry.values === null ? null : summarizeTariff(enteredTariff(entry.values, target, exponent), t)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {draft.scope !== null && (
            <div className="flex flex-col gap-1" data-testid="org-currency-switch-review-scope">
              <p className="font-medium">{t("operator.orgs.currency.switch.scope_heading", { currency: target })}</p>
              <ul className="flex flex-col gap-0.5 text-muted-foreground">
                {CONSUMER_PATHS.map((path) => (
                  <li key={path} className="flex flex-wrap gap-2">
                    <span>{t(`operator.orgs.currency.switch.scope_path_${path}`)}</span>
                    <span>{t(`operator.orgs.currency.switch.scope_state_${draft.scope!.consumer_states[path]}`)}</span>
                  </li>
                ))}
              </ul>
              {draft.scope.suspended && <p>{t("operator.orgs.currency.switch.scope_suspended", { currency: target })}</p>}
            </div>
          )}
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={draft.confirmed}
              onChange={(e) => edit((d) => setSwitchConfirmation(d, e.target.checked))}
            />
            <span className="text-sm font-medium">{t("operator.orgs.currency.switch.confirm_label", { old, currency: target })}</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setView("edit")}>{t("common.action.edit")}</Button>
            <Button type="button" onClick={activate} disabled={!draft.confirmed || frozen}>
              {state.activation.kind === "pending"
                ? t("operator.orgs.currency.switch.activating")
                : t("operator.orgs.currency.switch.activate_button", { currency: target })}
            </Button>
          </DialogFooter>
        </fieldset>
      </div>
    );
  }

  const targetId = `${id}-target`;
  const itemsHeadingId = `${id}-items-heading`;
  return (
    <div className="flex flex-col gap-4">
      {header}
      <fieldset disabled={frozen} className="flex min-w-0 flex-col gap-4" data-testid="org-currency-switch-edit">
        <div className="flex flex-wrap items-end gap-3">
          <Field className="w-56 gap-1">
            <FieldLabel htmlFor={targetId}>{t("operator.orgs.currency.switch.target_label")}</FieldLabel>
            <Select value={target ?? ""} onValueChange={(currency) => edit((d) => selectSwitchTarget(d, currency), { editing: null })} disabled={frozen || !eligible}>
              <SelectTrigger id={targetId} className="w-full">
                <SelectValue placeholder={t("operator.orgs.currency.switch.target_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {basis.target_choices.map((c) => (
                    <SelectItem key={c.currency} value={c.currency} disabled={c.verdict !== "eligible"}>
                      {c.verdict === "eligible" ? c.currency
                        : c.verdict === "ineligible" ? t("operator.orgs.currency.switch.target_current", { currency: c.currency })
                          : t("operator.orgs.currency.switch.target_not_ready", { currency: c.currency })}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
          {reload}
        </div>

        {!eligible && <p data-testid="org-currency-switch-no-target">{t("operator.orgs.currency.switch.no_target")}</p>}

        {target !== null && draft.scope !== null && (
          <section className="flex flex-col gap-3" data-testid="org-currency-switch-scope" data-invalid={scopeRefused || undefined}>
            <div className="flex flex-col gap-1">
              <h5 className="font-medium">{t("operator.orgs.currency.switch.scope_heading", { currency: target })}</h5>
              <p className="text-xs text-muted-foreground">{t("operator.orgs.currency.switch.scope_help", { currency: target })}</p>
            </div>
            {CONSUMER_PATHS.map((path) => {
              const scope = draft.scope!;
              const evidence = scope.evidence[path];
              const pathProblems = problems.filter((p) => "path" in p && p.path === path);
              const rowId = `${id}-scope-${path}`;
              const setEvidence = (patch: Partial<typeof evidence>) => edit((d) => setSwitchScopeEvidence(d, path, { ...evidence, ...patch }));
              return (
                <div key={path} className="flex flex-col gap-1 rounded-md border p-2" data-testid={`org-currency-switch-scope-${path}`}>
                  <div className="flex flex-wrap items-end gap-2">
                    <Field className="w-56 gap-1">
                      <FieldLabel className="text-xs" htmlFor={`${rowId}-state`}>{t(`operator.orgs.currency.switch.scope_path_${path}`)}</FieldLabel>
                      <Select value={scope.consumer_states[path]} onValueChange={(next) => edit((d) => setSwitchConsumerState(d, path, next))} disabled={frozen}>
                        <SelectTrigger id={`${rowId}-state`} className="w-full"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {CONSUMER_STATES.map((s) => (
                              <SelectItem key={s} value={s}>{t(`operator.orgs.currency.switch.scope_state_${s}`)}</SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field className="w-48 gap-1">
                      <FieldLabel className="text-xs" htmlFor={`${rowId}-ref`}>{t("operator.orgs.currency.switch.scope_reference")}</FieldLabel>
                      <Input id={`${rowId}-ref`} value={evidence.ref} onChange={(e) => setEvidence({ ref: e.target.value })} />
                    </Field>
                    <Field className="w-48 gap-1">
                      <FieldLabel className="text-xs" htmlFor={`${rowId}-recorded`}>{t("operator.orgs.currency.switch.scope_recorded")}</FieldLabel>
                      <Input id={`${rowId}-recorded`} value={evidence.recorded_at} onChange={(e) => setEvidence({ recorded_at: e.target.value })} />
                    </Field>
                  </div>
                  {pathProblems.some((p) => p.kind === "evidence_incomplete") && <FieldError>{t("operator.orgs.currency.switch.scope_evidence_incomplete")}</FieldError>}
                  {pathProblems.some((p) => p.kind === "evidence_nul_character") && <FieldError>{t("operator.orgs.currency.switch.invalid_scope")}</FieldError>}
                </div>
              );
            })}
            <div className="flex items-center gap-2">
              <Switch
                id={`${id}-suspended`}
                checked={draft.scope.suspended}
                onCheckedChange={(checked) => edit((d) => setSwitchScopeSuspended(d, checked))}
                disabled={frozen}
              />
              <Label htmlFor={`${id}-suspended`}>{t("operator.orgs.currency.switch.scope_suspended", { currency: target })}</Label>
            </div>
          </section>
        )}

        {target !== null && (
          <section className="flex flex-col gap-3" data-testid="org-currency-switch-items">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex flex-col gap-1">
                <h5 id={itemsHeadingId} className="font-medium">{t("operator.orgs.currency.switch.items_heading", { currency: target })}</h5>
                <p className="text-xs text-muted-foreground">{t("operator.orgs.currency.switch.items_help", { old, currency: target })}</p>
              </div>
              <Button type="button" size="sm" variant="outline" className="h-auto min-h-8 max-w-full py-1 whitespace-normal" onClick={() => edit(copyAllSwitchItemRules)}>
                {t("operator.orgs.currency.switch.copy_all")}
              </Button>
            </div>
            <div role="region" aria-labelledby={itemsHeadingId} className={TABLE_SCROLL}>
              <Table>
                <TableBody>
                  {items.map(({ ref, entry, name, tariff, paidMode }) => {
                    const label = itemLabel(t, name);
                    const own = problemsOf(ref);
                    const open = sameRef(ref, state.editing);
                    const copyable = tariffMode(tariff) !== null;
                    const ruleMessage = entry.values !== null && own.some((p) => p.kind === "rule_invalid")
                      ? validatePricingForm(entry.values, t) ?? t("operator.orgs.currency.switch.invalid_rate", { item: label })
                      : null;
                    const refused = sameRef(ref, highlighted);
                    return [
                      <TableRow key={refKey(ref)} data-testid={`org-currency-switch-item-${refKey(ref)}`} data-invalid={refused || undefined} aria-invalid={refused || undefined}>
                        <TableCell className="font-medium">{label}</TableCell>
                        <TableCell className="whitespace-normal">{currentPrices(t, tariff)}</TableCell>
                        <TableCell>
                          {(paidMode === "paid" || paidMode === "free") && <Badge variant={paidMode === "free" ? "success" : "neutral"}>{t("billing.tariffs.mode_badge", { mode: paidMode })}</Badge>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entry.status === "entered" ? "success" : entry.status === "changed" ? "warning" : "outline"} data-testid="org-currency-switch-item-status">
                            {t(entry.status === "entered" ? "operator.orgs.currency.switch.item_entered"
                              : entry.status === "changed" ? "operator.orgs.currency.switch.item_changed" : "operator.orgs.currency.switch.item_not_entered")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap justify-end gap-1">
                            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(open ? null : ref)} aria-expanded={open}>
                              {t(open ? "common.action.collapse" : "common.action.edit")}
                            </Button>
                            <Button type="button" size="sm" variant="ghost" onClick={() => edit((d) => copySwitchItemRules(d, ref))} disabled={frozen || !copyable}>
                              {t("operator.orgs.currency.switch.copy_one")}
                            </Button>
                            <Button type="button" size="sm" variant="ghost" onClick={() => edit((d) => noChargeSwitchItem(d, ref))}>
                              {t("operator.orgs.currency.switch.item_no_rules")}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>,
                      <TableRow key={`${refKey(ref)}-detail`} className="border-0 hover:bg-transparent">
                        <TableCell colSpan={5} className="whitespace-normal pt-0">
                          <div className="flex flex-col gap-2">
                            {entry.source?.kind === "copy" && (
                              <FieldDescription>{t("operator.orgs.currency.switch.copied_note", { old: entry.source.from, currency: target })}</FieldDescription>
                            )}
                            {ruleMessage !== null && <FieldError>{ruleMessage}</FieldError>}
                            {own.some((p) => p.kind === "paid_requires_positive_rate") && ref.kind === "unit" && (
                              <FieldError>{t("operator.orgs.currency.switch.paid_needs_rate", { unit: ref.unit_id, currency: target })}</FieldError>
                            )}
                            {open && (
                              <div data-testid={`org-currency-switch-editor-${refKey(ref)}`}>
                                <PricingForm
                                  value={entry.values ?? { currency: target, rules: [] }}
                                  onChange={(values) => edit((d) => enterSwitchItem(d, ref, values))}
                                  tenant={{ status: "known", currency: target, writable: true }}
                                  idPrefix={`switch-${refKey(ref)}`}
                                />
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>,
                    ];
                  })}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => setView("review")} disabled={frozen || problems.length > 0}>
                {t("operator.orgs.currency.switch.review_button")}
              </Button>
            </DialogFooter>
          </section>
        )}
      </fieldset>
    </div>
  );
}
